import { ofType } from 'redux-observable';
import { mergeMap, map, filter, concatMap, EMPTY } from 'rxjs';
import type { AppEpic } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { of } from 'rxjs';
import {
  inputIsNotNullOrUndefined,
  isScopeDefined,
  isWorklistActive,
  maybeGetComponentProps,
  topMemInUrl,
} from 'src/utils/Functions/epicsFunctions';
import { get, isEmpty } from 'lodash';
import { ConfDefnComponentType, EditableOverTimeGridComponent } from 'configuration-schemas/confdefnComponents';
import {
  fetchOverTimeData,
  updateSelectedItem,
  refreshOvertimeData,
  fetchCompanionData,
  fetchMassEditData,
  fetchWorklistOverTimeData,
} from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetByStyle.slice';

import { updateSelectedItem as worklistUpdateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import { completeItemUndo } from 'src/pages/AssortmentBuild/Planning.slice';
import { receiveScopeRefreshTrigger } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from '../Planning.types';
import { REQUEST_EXTERNAL_DATA_REFRESH } from 'src/components/higherOrder/GlobalDataRefresh';

function fetchTopMember(state: AppState, worklistSelectedItem?: string): string {
  const flowSheetSelectedItem = !isEmpty(state.pages.assortmentBuild.flowSheet.selectedItem)
    ? state.pages.assortmentBuild.flowSheet.selectedItem
    : null;
  const worklistItem = worklistSelectedItem;
  const urlTopMember = topMemInUrl();
  return flowSheetSelectedItem ?? worklistItem ?? urlTopMember ?? '';
}

export const flowSheetByStyleOvertimeLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      updateSelectedItem.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
      refreshOvertimeData.type,
      completeItemUndo.type,
      worklistUpdateSelectedItem.type,
    ),
    map(() => maybeGetComponentProps<EditableOverTimeGridComponent>(state$.value, ConfDefnComponentType.flowSheet)),
    filter(inputIsNotNullOrUndefined),
    filter(() => isScopeDefined(state$.value.scope)),
    mergeMap(({ dataApi, topMembers, defns }) => {
      const worklistSelectedItem = topMembers;
      const currentSelectedItem = fetchTopMember(state$.value, worklistSelectedItem);

      if (isEmpty(currentSelectedItem)) {
        return EMPTY;
      }

      // we allow a third element to be passed in from `componentProps.defns.models` in flowsheet/pricing,
      // to override the modeldefn, in order to support different pivot runs
      const maybeThirdModel = defns && defns.models ? get(defns.models, '2', undefined) : undefined;

      return isWorklistActive(state$.value)
        ? of(fetchWorklistOverTimeData(currentSelectedItem, dataApi, maybeThirdModel))
        : of(fetchOverTimeData(currentSelectedItem, dataApi, maybeThirdModel));
    }),
  );
};

export const flowSheetByStyleCompanionLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(receiveScopeRefreshTrigger.type, receiveFilterStateAfterSubmission.type, REQUEST_EXTERNAL_DATA_REFRESH),
    map(() => maybeGetComponentProps<EditableOverTimeGridComponent>(state$.value, ConfDefnComponentType.flowSheet)),
    filter(inputIsNotNullOrUndefined),
    filter(() => !isWorklistActive(state$.value)),
    filter(() => isScopeDefined(state$.value.scope)),
    concatMap(({ defns, hideCompanion }) => {
      if (hideCompanion) {
        return of(refreshOvertimeData());
      }

      const companionModel = defns.models[0];
      const massEditModel = defns.models[1];
      const actions = [
        ...(companionModel ? [fetchCompanionData(companionModel)] : []),
        ...(massEditModel ? [fetchMassEditData(massEditModel)] : []),
      ];

      return of(...actions);
    }),
  );
};
